/**
 * 模块注册表（前端）
 * ------------------------------------------------------------
 * 解析 VITE_EMA_MODULES 白名单，输出启用模块集合、聚合路由与开关查询。
 * 壳层/底栏/顶栏一律通过 isModuleEnabled 判断模块入口显隐，不再各自硬编码。
 * 跨模块组件/函数引用一律走 provides 插槽（slotComponent/slotValue），
 * 消费方在插槽未提供时降级渲染，编译期互不依赖。
 */
import { defineAsyncComponent, type AsyncComponentLoader, type Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import type { FeatureModule } from './types'
import { ALL_MODULES } from './modules.config'

/** 未设置 = 全部启用；设置 = 逗号分隔 code 白名单；'none' = 纯底座（无业务模块） */
function parseWhitelist(): Set<string> | null {
  const raw = (import.meta.env.VITE_EMA_MODULES as string | undefined)?.trim()
  if (!raw) return null
  return new Set(raw.split(',').map(s => s.trim()).filter(s => s && s !== 'none'))
}

function resolveEnabled(): FeatureModule[] {
  const whitelist = parseWhitelist()
  const byCode = new Map(ALL_MODULES.map(m => [m.code, m]))
  for (const code of whitelist ?? []) {
    if (!byCode.has(code)) console.warn(`[EMA] VITE_EMA_MODULES 配置了未登记的模块: ${code}`)
  }
  const on = new Set<string>()
  const visit = (m: FeatureModule) => {
    if (on.has(m.code)) return
    on.add(m.code)
    for (const dep of m.dependsOn ?? []) {
      const d = byCode.get(dep)
      if (d) visit(d)
      else console.warn(`[EMA] 模块 ${m.code} 依赖未登记的模块: ${dep}`)
    }
  }
  for (const m of ALL_MODULES) if (!whitelist || whitelist.has(m.code)) visit(m)
  return ALL_MODULES.filter(m => on.has(m.code))
}

export const ENABLED_MODULES: FeatureModule[] = resolveEnabled()

const enabledCodes = new Set(ENABLED_MODULES.map(m => m.code))

/** 模块是否启用（壳层入口显隐、页面内跳转前判断统一走这里） */
export function isModuleEnabled(code: string): boolean {
  return enabledCodes.has(code)
}

/** 聚合全部启用模块的路由（注册进主路由表，catch-all 之前） */
export function collectModuleRoutes(): RouteRecordRaw[] {
  return ENABLED_MODULES.flatMap(m => m.routes ?? [])
}

/** 启用模块贡献的全部插槽（后者覆盖前者，重名时 warn） */
const PROVIDES = (() => {
  const out = new Map<string, unknown>()
  for (const m of ENABLED_MODULES) {
    for (const [name, value] of Object.entries(m.provides ?? {})) {
      if (out.has(name)) console.warn(`[EMA] 插槽 ${name} 被重复提供，后者覆盖前者`)
      out.set(name, value)
    }
  }
  return out
})()

/** 插槽是否已有模块提供 */
export function hasSlot(name: string): boolean {
  return PROVIDES.has(name)
}

/**
 * 取插槽异步组件；未提供返回 null，消费方自行降级（隐藏入口或提示）。
 * 用法：const KnowledgePanel = slotComponent('knowledge.panel')
 */
export function slotComponent(name: string): Component | null {
  const loader = PROVIDES.get(name)
  if (!loader) return null
  if (typeof loader !== 'function') {
    console.warn(`[EMA] 插槽 ${name} 的值不是异步组件加载器`)
    return null
  }
  return defineAsyncComponent(loader as AsyncComponentLoader) as Component
}

/** 取插槽纯值（如路由处理函数）；未提供返回 null */
export function slotValue<T = unknown>(name: string): T | null {
  return (PROVIDES.get(name) as T | undefined) ?? null
}

/** 路由首段 → 所属模块 code（由登记模块的路由派生，用于菜单/入口过滤） */
const PREFIX_OWNER = (() => {
  const out = new Map<string, string>()
  for (const m of ALL_MODULES) {
    for (const r of m.routes ?? []) {
      const path = typeof r.path === 'string' ? r.path : ''
      const seg = path.match(/^\/([^/]+)/)?.[1]
      if (seg && !out.has(seg)) out.set(seg, m.code)
    }
  }
  return out
})()

/**
 * 菜单叶子对应的路由是否可见：能定位到所属模块且模块未启用时隐藏；
 * 无法定位（底座路由、redirect 项）一律可见。
 */
export function menuRouteVisible(path: string): boolean {
  const seg = path.match(/^\/([^/]+)/)?.[1]
  if (!seg) return true
  const owner = PREFIX_OWNER.get(seg)
  return owner ? enabledCodes.has(owner) : true
}
